import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import "./websiteBuilderBanner.scss";
import { WEBSITE_BUILDER_Z_LAYERS } from "../../utility/websiteBuilderZLayers";
import { ButtonNew as Button } from "components/buttonNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCopy, faDesktop, faMobile, faTablet } from "@fortawesome/pro-regular-svg-icons";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { websiteBuilderPublishWebsiteVersion, websiteBuilderRestoreWebsiteVersion, websiteBuilderSetDeviceView, websiteBuilderSetMode } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import Spin from "components/spin/spin";
import { Badge } from "components/badge/Badge";
import { WebsiteBuilderSkeleton } from "../../components/skeleton/WebsiteBuilderSkeleton";
import { useHistory } from "react-router";
import { WebsiteBuilderBannerPageDropdown } from "./pageDropdown/WebsiteBuilderBannerPageDropdown";
import Portal from "elements/Portal";
import useModal from "hooks/modals/useModal";
import Sheet from "components/sheet/Sheet";
import Input from "components/form/input/Input";
import { showError, showSuccess } from "redux/actions/ui";
import { WEBSITE_BUILDER_PORTAL } from "../../WebsiteBuilderEditor";
import classNames from "classnames";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { TWebsiteBuilderDevice, WEBSITE_BUILDER_MOBILE_END_BREAKPOINT, WEBSITE_BUILDER_MOBILE_START_BREAKPOINT, WEBSITE_BUILDER_TABLET_END_BREAKPOINT, WEBSITE_BUILDER_TABLET_START_BREAKPOINT } from "../../website/shared/device";

interface IActiveProps {
  type: "active";
}

interface ISkeletonProps {
  type: "skeleton"
}

type TWebsiteBuilderBannerProps = IActiveProps | ISkeletonProps;

interface IWebsiteBuilderBannerPublishedModalState {
  isOpen: boolean;
  domain: string;
  wasNotPublished: boolean;
}

interface IWebsiteBuilderDeviceOption {
  device: TWebsiteBuilderDevice;
  tooltip: string;
}

export const WEBSITE_BUILDER_BANNER = "website-builder-banner";

export function WebsiteBuilderBanner(props: TWebsiteBuilderBannerProps) {
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const history = useHistory();

  const facilityLongName = useAppSelector(store => store.facilityStore.facility?.long_name ?? "");

  const websitePublishedVersionId = useAppSelector(store => store.websiteBuilderStore.config.website?.published_version_id);
  const faviconUrl = useAppSelector(store => store.websiteBuilderStore.config.website?.favicon_url);
  const logoUrl = useAppSelector(store => store.websiteBuilderStore.config.website?.logo_url);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);
  const mode = useAppSelector(store => store.websiteBuilderStore.editor.mode);
  const combinedAutoSaveStatus = useAppSelector(store => store.websiteBuilderStore.editor.autoSaveStatusState?.combined);
  const viewingPastVersion = useAppSelector(store => store.websiteBuilderStore.editor.viewingPastVersion);
  const websiteVersionId = useAppSelector(store => store.websiteBuilderStore.editor.websiteVersion?.id);
  const header = useAppSelector(store => store.websiteBuilderStore.editor.header);
  const footer = useAppSelector(store => store.websiteBuilderStore.editor.footer);
  const rootCssVariables = useAppSelector(store => store.websiteBuilderStore.editor.rootCssVariables);
  const pages = useAppSelector(store => store.websiteBuilderStore.editor.pages);
  const pageMap = useAppSelector(store => store.websiteBuilderStore.editor.pageMap);
  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const navigationMenuItems = useAppSelector(store => store.websiteBuilderStore.editor.navigationMenuItems);
  const forms = useAppSelector(store => store.websiteBuilderStore.editor.forms);
  const iconAssets = useAppSelector(store => store.websiteBuilderStore.editor.iconAssets);

  const {
    state: publishedModalState,
    updateModal: updatePublishedModal,
    closeModal: closePublishedModal,
  } = useModal<IWebsiteBuilderBannerPublishedModalState>({
    isOpen: false,
    domain: null,
    wasNotPublished: false,
    clearOnClose: true,
  });

  const activePage = pageMap.get(activePageId);

  const savedCombinedAutoSaveStatus = combinedAutoSaveStatus === "saved";

  const deviceOptions: IWebsiteBuilderDeviceOption[] = useMemo(() => {
    return (
      [
        {
          device: "desktop",
          tooltip: t("Desktop (Primary)"),
        },
        {
          device: "tablet",
          tooltip: t("Tablet ({{start}}px to {{end}}px)", { start: WEBSITE_BUILDER_TABLET_START_BREAKPOINT, end: WEBSITE_BUILDER_TABLET_END_BREAKPOINT }),
        },
        {
          device: "mobile",
          tooltip: t("Mobile ({{start}}px to {{end}}px)", { start: WEBSITE_BUILDER_MOBILE_START_BREAKPOINT, end: WEBSITE_BUILDER_MOBILE_END_BREAKPOINT }),
        },
      ]
    );
  }, []);

  const changeModeDisplayText = useMemo(() => {
    if (mode === "preview") {
      return t("Design");
    } else if (mode === "design") {
      return t("Preview");
    } else {
      return "";
    }
  }, [mode]);

  function changeDeviceView(device: TWebsiteBuilderDevice) {
    dispatch(websiteBuilderSetDeviceView({ deviceView: device }));
  }

  function changeMode() {
    if (mode === "preview") {
      dispatch(websiteBuilderSetMode({ mode: "design" }));
    } else if (mode === "design") {
      dispatch(websiteBuilderSetMode({ mode: "preview" }));
    }
  }

  function handlePublishSuccess(domain: string, wasNotPublished: boolean) {
    updatePublishedModal({ isOpen: true, domain, wasNotPublished });
  }

  function publishWebsiteVersion() {
    void dispatch(websiteBuilderPublishWebsiteVersion(header, pages, navigationMenuItems, footer, websiteVersionId, websitePublishedVersionId, faviconUrl, logoUrl, facilityLongName, rootCssVariables, forms, iconAssets, handlePublishSuccess, t));
  }

  function clearViewingPastVersionUrl() {
    history.replace("/admin/website-builder-editor");
  }

  function restoreWebsiteVersion() {
    void dispatch(websiteBuilderRestoreWebsiteVersion(header, pages, footer, websiteVersionId, clearViewingPastVersionUrl, t));
  }

  function renderWebsiteStatusBadge() {
    if (props.type !== "active" || viewingPastVersion) {
      return null;
    }

    let content: JSX.Element = null;

    switch (combinedAutoSaveStatus) {
      case "saving": {
        content =
          <div className="banner-page-saving">
            {t("Saving")} <Spin className="banner-page-saving-spin" />
          </div>;
        break;
      }
      case "unsaved-changes": {
        content = <div>{t("Unsaved changes")}</div>;
        break;
      }
      case "error": {
        content = <div>{t("Error saving")}</div>;
        break;
      }
      default: {
        content = <div>{t("Saved")}</div>;
      }
    }

    return (
      <Badge type="gray">
        {content}
      </Badge>
    );
  }

  function getDeviceIcon(device: TWebsiteBuilderDevice) {
    if (device === "desktop") {
      return faDesktop;
    } else if (device === "tablet") {
      return faTablet;
    } else if (device === "mobile") {
      return faMobile;
    } else {
      return null;
    }
  }

  return (
    <div
      id={WEBSITE_BUILDER_BANNER}
      style={{
        zIndex: WEBSITE_BUILDER_Z_LAYERS.shell.ui,
      }}
    >
      <div className="banner-content">
        <div>
          <img
            className="banner-icon"
            src="/public/images/tee_on_icon.svg"
            onClick={() => history.push("/admin/website-builder")}
          />
          {props.type === "active" ? (
            <WebsiteBuilderBannerPageDropdown />
          ) : (
            <WebsiteBuilderSkeleton width={100} height={10} />
          )}
        </div>
        <div>
          {props.type === "active" ? (
            <>
              <div className="banner-page-title">{activePage?.title}</div>
              {renderWebsiteStatusBadge()}
            </>
          ) : (
            <WebsiteBuilderSkeleton width={175} height={10} />
          )}
        </div>
        <div>
          {props.type === "active" ? (
            <>
              <div className="banner-device-options">
                {deviceOptions.map((deviceOption, index) => {
                  const deviceOptionSelected = deviceOption.device === deviceView;

                  return (
                    <OverlayTrigger
                      key={index}
                      placement="bottom"
                      delay={{
                        show: 500,
                        hide: 100,
                      }}
                      overlay={
                        <Tooltip id={`website-builder-banner-device-option-tooltip-${deviceOption.device}`}>
                          {deviceOption.tooltip}
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon
                        className={classNames("banner-device-option", {
                          "banner-device-option-selected": deviceOptionSelected,
                        })}
                        icon={getDeviceIcon(deviceOption.device)}
                        onClick={() => changeDeviceView(deviceOption.device)}
                      />
                    </OverlayTrigger>
                  );
                })}
              </div>
              <div
                className="banner-vertical-divider"
                style={{
                  marginRight: viewingPastVersion ? 36 : 18,
                }}
              />
              <div className="banner-actions">
                {viewingPastVersion ? (
                  <Button
                    size="small"
                    onClick={restoreWebsiteVersion}
                    disabled={!savedCombinedAutoSaveStatus}
                  >
                    {t("Restore")}
                  </Button>
                ) : (
                  <>
                    <Button
                      size="small"
                      type="text"
                      onClick={changeMode}
                    >
                      {changeModeDisplayText}
                    </Button>
                    <Button
                      size="small"
                      onClick={publishWebsiteVersion}
                      disabled={!savedCombinedAutoSaveStatus}
                    >
                      {t("Publish")}
                    </Button>
                  </>
                )}
              </div>
            </>
          ) : (
            <WebsiteBuilderSkeleton width={360} height={10} />
          )}
        </div>
      </div>

      <Portal isMounted={publishedModalState.isOpen} parentId={WEBSITE_BUILDER_PORTAL}>
        <Sheet
          title={
            <div className="website-builder-banner-published-modal-title">
              <div className="published-modal-title-success-outer-circle">
                <div className="published-modal-title-success-inner-circle">
                  <div className="published-modal-title-success-inner-circle-check-icon">
                    <FontAwesomeIcon icon={faCircleCheck} />
                  </div>
                </div>
              </div>
              <h1>{t("Website is published")}</h1>
              {publishedModalState.wasNotPublished ? (
                <p>{t("Success! Your website has been published. Since it wasn't published previously it may take some time to go live.")}</p>
              ) : (
                <p>{t("Success! Your website has been published and is live.")}</p>
              )}
            </div>
          }
          open={publishedModalState.isOpen}
          size="small"
          closable
          okText={t("View site")}
          onOk={() => {
            window.open(`https://${publishedModalState.domain}`, "_blank");

            closePublishedModal();
          }}
          cancelText={t("Close")}
          onCancel={closePublishedModal}
        >
          <div className="website-builder-banner-published-modal">
            <Input
              label={t("Website domain")}
              placeholder={publishedModalState.domain}
              suffix={
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(publishedModalState.domain);

                      dispatch(showSuccess(t("Copied domain to clipboard")));
                    } catch {
                      dispatch(showError(t("Error copying domain to clipboard")));
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faCopy} /> {t("Copy")}
                </button>
              }
            />
          </div>
        </Sheet>
      </Portal>
    </div>
  );
}
